import React, {
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import PsdFilter from '@v2/components/PsdFilter';
import { FilterOption } from '@v2/components/PsdFilter/types';
import { FilterContext } from '@components/FilterBar/FilterProvider';
import { constants } from '@constants/Constants';
import { useDynamicGraphQLQuery } from '@hooks/useDynamicGraphQLQuery';
import { useFilterV2, useSpecificFilter } from '@hooks/useFilterV2';

export const INTEGRATED_PRODUCT_FILTER_ID = 'integratedProduct';
const DEFAULT_INTEGRATED_PRODUCT_VALUE = 1; // PepsiConnect

interface IntegratedProductFilterProps {
  /** Optional custom filterId for separate Redux state (defaults to INTEGRATED_PRODUCT_FILTER_ID) */
  filterId?: string;
}

interface AppFilter {
  productId: number;
  modelId: number;
  markets: {
    marketId: number;
    marketName: string;
    apps: { appId: number; appName: string }[];
  }[];
}

/**
 * Compute filter options from GraphQL response filtered by current context
 */
const computeOptions = (
  gsSOAppFilters: AppFilter[],
  selectedProduct: any,
  selectedModel: any,
  selectedMarket: any
): FilterOption[] => {
  if (
    !gsSOAppFilters ||
    !selectedProduct ||
    !selectedModel ||
    !selectedMarket
  ) {
    return [];
  }

  const productFilter = gsSOAppFilters.find(
    (filter) =>
      filter.productId === selectedProduct.productId &&
      filter.modelId === selectedModel.id
  );

  if (!productFilter) {
    return [];
  }

  const marketConfig = productFilter.markets.find(
    (market) => market.marketId === selectedMarket.marketId
  );
  const allMarketConfig = !marketConfig
    ? productFilter.markets.find((market) => market.marketId === 999999)
    : null;
  const activeMarketConfig = marketConfig || allMarketConfig;

  if (!activeMarketConfig?.apps) {
    return [];
  }

  return activeMarketConfig.apps
    .map((app) => ({
      value: app.appId,
      label: app.appName,
    }))
    .sort((a: FilterOption, b: FilterOption) => {
      if (String(a.label).toLowerCase() === 'all') {
        return -1;
      }
      if (String(b.label).toLowerCase() === 'all') {
        return 1;
      }
      return Number(a.value) - Number(b.value);
    });
};

/**
 * IntegratedProductFilter - Integrated Product filter component using PsdFilter
 *
 * Self-contained component that fetches getGSSOAppFilters data,
 * computes context-aware options, and renders via PsdFilter.
 * Registered in DatoCMS as a CustomReactComponent.
 */
const IntegratedProductFilter: React.FC<IntegratedProductFilterProps> = ({
  filterId = INTEGRATED_PRODUCT_FILTER_ID,
}) => {
  const { selectedProduct, selectedMarket, selectedModel } =
    useContext(FilterContext);

  // Redux filter management - pre-register filter on mount
  const { updateFilterValue, updateFilterEnabled, updateFilterLoading } =
    useFilterV2();

  // Get current Redux value to check if there's already a persisted selection
  const { selectedValue: currentReduxValue } = useSpecificFilter(filterId);

  // Capture initial Redux value at mount to determine if we should set default
  const initialReduxValueRef = useRef(currentReduxValue);

  // Pre-register the filter in filterSliceV2 on mount
  // Only set default value if there's no existing value (preserves selection on tab switch)
  useEffect(() => {
    updateFilterEnabled(filterId, true);
    updateFilterLoading(filterId, false);
    // Only set default if no value exists in Redux (first load or after reset)
    if (
      initialReduxValueRef.current === null ||
      initialReduxValueRef.current === undefined
    ) {
      updateFilterValue(filterId, DEFAULT_INTEGRATED_PRODUCT_VALUE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: gsSOAppFiltersData, isLoading: gsSOAppFilterLoading } =
    useDynamicGraphQLQuery('getGSSOAppFilters');

  const gsSOAppFilters: AppFilter[] =
    gsSOAppFiltersData?.getGSSOAppFilters?.data || [];

  // Market condition checks
  const isTurkeyOrArgentinaMkt =
    selectedMarket?.marketId === constants.markets.turkey ||
    selectedMarket?.marketId === constants.markets.argentina;
  const allMarket =
    selectedMarket?.sectorId === constants.sectors.Europe &&
    selectedMarket?.marketId === constants.globalIDs.marketId;
  const isMexicoMarket = selectedMarket?.marketId === constants.markets.mexico;

  // Determine if filter should be enabled (user can interact with it)
  const isFilterEnabled = isTurkeyOrArgentinaMkt || allMarket;

  // Sync enabled state to Redux when market conditions change
  // This ensures the filter is properly disabled/enabled when switching markets
  useEffect(() => {
    updateFilterEnabled(filterId, isFilterEnabled);

    // When filter becomes disabled (non-Turkey/Argentina market), reset to PepsiConnect default
    if (!isFilterEnabled) {
      updateFilterValue(filterId, DEFAULT_INTEGRATED_PRODUCT_VALUE);
    }
  }, [filterId, isFilterEnabled, updateFilterEnabled, updateFilterValue]);

  // Compute filtered options based on current context
  const processedOptions = useMemo(
    () =>
      computeOptions(
        gsSOAppFilters,
        selectedProduct,
        selectedModel,
        selectedMarket
      ),
    [gsSOAppFilters, selectedProduct, selectedModel, selectedMarket]
  );

  // Compute the default selected value based on business rules
  const defaultSelectedValue = useMemo(() => {
    if (processedOptions.length === 0) {
      return undefined;
    }

    // Mexico market defaults to CDFO
    if (isMexicoMarket) {
      const cdfoOption = processedOptions.find((opt) => opt.label === 'CDFO');
      if (cdfoOption) {
        return cdfoOption.value;
      }
    }

    // Default to PepsiConnect, fallback to first option
    const pepsiConnectOption = processedOptions.find(
      (opt) => opt.value === DEFAULT_INTEGRATED_PRODUCT_VALUE
    );
    return pepsiConnectOption?.value ?? processedOptions[0]?.value;
  }, [processedOptions, isMexicoMarket]);

  // Use ref to store options to prevent effect re-runs when user selects a value
  const optionsRef = useRef(processedOptions);
  optionsRef.current = processedOptions;

  // Stable callback that returns computed options - only changes when market/product/model changes
  const staticOptionsFunction = useCallback(
    () => optionsRef.current,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedMarket?.marketId, selectedProduct?.productId, selectedModel?.id]
  );

  if (gsSOAppFilterLoading) {
    return null;
  }

  return (
    <PsdFilter
      filterId={filterId}
      label="Integrated Product"
      staticOptionsFunction={staticOptionsFunction}
      enabled={isFilterEnabled}
      selectedValue={isFilterEnabled ? undefined : defaultSelectedValue}
      persistValue={isFilterEnabled}
    />
  );
};

export default IntegratedProductFilter;
